/**
 * Apple In-App Purchase flow for iOS (Capacitor).
 * When running in the native iOS app, use the LumaIAP plugin to purchase then verify with the backend.
 */

import { supabase } from '@/integrations/supabase/client';
import { isNativeIOS } from './capacitor';

/** Product ID for Pro subscription in App Store Connect. Must match the IAP product. */
export const APPLE_IAP_PRO_PRODUCT_ID = 'pro_monthly';

export interface ApplePurchaseResult {
  receiptData: string;
  productId: string;
}

/**
 * Call the native LumaIAP plugin to start a purchase and return receipt data.
 * The native side (Swift) must implement StoreKit 2, then return base64 receipt or transaction data.
 */
async function purchaseViaNativePlugin(productId: string): Promise<ApplePurchaseResult | null> {
  const cap = (typeof window !== 'undefined' && window.Capacitor) as {
    Plugins?: {
      LumaIAP?: { purchase: (opts: { productId: string }) => Promise<{ receiptData: string; productId: string }> };
      LumaIAPPlugin?: { purchase: (opts: { productId: string }) => Promise<{ receiptData: string; productId: string }> };
    };
  } | undefined;
  const plugin = cap?.Plugins?.LumaIAP ?? cap?.Plugins?.LumaIAPPlugin;
  if (!plugin?.purchase) return null;
  const result = await plugin.purchase({ productId });
  if (result?.receiptData) return { receiptData: result.receiptData, productId: result.productId || productId };
  return null;
}

/**
 * Verify the Apple receipt with our backend and activate Pro for the current user.
 */
async function verifyAppleReceipt(receiptData: string, productId: string): Promise<{ success: boolean; error?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { success: false, error: 'Not authenticated' };

  const { data, error } = await supabase.functions.invoke('verify-apple-purchase', {
    body: { receiptData, productId },
  });

  if (error) return { success: false, error: error?.message ?? 'Verification failed' };
  if (data?.error) return { success: false, error: typeof data.error === 'string' ? data.error : 'Verification failed' };
  return { success: true };
}

/**
 * Run the full Apple IAP flow on iOS: native purchase then backend verification.
 * Returns the same shape as upgradeToPro for useSubscription.
 */
export async function purchaseProWithAppleIAP(): Promise<{ success: boolean; checkoutUrl?: string; error?: string }> {
  if (!isNativeIOS()) {
    return { success: false, error: 'Apple IAP is only available in the iOS app' };
  }

  const purchase = await purchaseViaNativePlugin(APPLE_IAP_PRO_PRODUCT_ID);
  if (!purchase) {
    return { success: false, error: 'In-App Purchase is not available. Make sure the LumaIAP plugin is installed and App Store Connect has the product.' };
  }

  const verified = await verifyAppleReceipt(purchase.receiptData, purchase.productId);
  if (!verified.success) return { success: false, error: verified.error ?? 'Verification failed' };
  return { success: true };
}
